import type {
  CreateResult,
  DeleteResult,
  GetResult,
  ListResult,
  ServiceContext,
  UpdateResult,
} from "@prolarus/shared";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryUpdate,
} from "./_shared";

export interface PermissionRow {
  id: string;
  company_id: string;
  name: string;
  code: string;
  description: string | null;
  category: string | null;
  is_system: boolean;
  is_active: boolean;
  created: string;
  modified: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface PermissionUserRow {
  profile_id: string;
  full_name: string | null;
  avatar_url: string | null;
  permission_code: string;
  permission_name: string;
  category: string | null;
  assigned_at: string;
}

export interface PermissionGroupRow {
  group_id: string;
  group_name: string;
  group_description: string | null;
  is_active: boolean;
  assigned_at: string;
}

// Mirrors the prior permissionAdminService surface. Routes live under
// /api/permission-catalog/* (distinct from /api/permissions which is the
// user-perspective view-backed lens). _companyId / _context / _snapshot
// args are preserved for signature compat with the prior service.
export const permissionAdminService = {
  list(_companyId: string): Promise<ListResult<PermissionRow>> {
    return tryList<PermissionRow>("/api/permission-catalog");
  },

  get(id: string): Promise<GetResult<PermissionRow>> {
    return tryGet<PermissionRow>(
      `/api/permission-catalog/${encodeURIComponent(id)}`,
    );
  },

  create(
    data: {
      name: string;
      code: string;
      description?: string | null;
      category?: string | null;
    },
    _context: ServiceContext,
  ): Promise<CreateResult<PermissionRow>> {
    return tryCreate<PermissionRow>("/api/permission-catalog", data);
  },

  update(
    id: string,
    data: Partial<{
      name: string;
      code: string;
      description: string | null;
      category: string | null;
      is_active: boolean;
    }>,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<UpdateResult<PermissionRow>> {
    return tryUpdate<PermissionRow>(
      `/api/permission-catalog/${encodeURIComponent(id)}`,
      data,
    );
  },

  delete(
    id: string,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    return tryDelete(`/api/permission-catalog/${encodeURIComponent(id)}`);
  },

  listUsers(
    permissionId: string,
    _companyId: string,
  ): Promise<ListResult<PermissionUserRow>> {
    return tryList<PermissionUserRow>(
      `/api/permission-catalog/${encodeURIComponent(permissionId)}/users`,
    );
  },

  listGroups(
    permissionId: string,
    _companyId: string,
  ): Promise<ListResult<PermissionGroupRow>> {
    return tryList<PermissionGroupRow>(
      `/api/permission-catalog/${encodeURIComponent(permissionId)}/groups`,
    );
  },

  removeUser(
    permissionId: string,
    profileId: string,
    _companyId: string,
  ): Promise<DeleteResult> {
    return tryDelete(
      `/api/permission-catalog/${encodeURIComponent(permissionId)}/users/${encodeURIComponent(profileId)}`,
    );
  },

  removeGroup(
    permissionId: string,
    groupId: string,
    _companyId: string,
  ): Promise<DeleteResult> {
    return tryDelete(
      `/api/permission-catalog/${encodeURIComponent(permissionId)}/groups/${encodeURIComponent(groupId)}`,
    );
  },

  bulkCreateSystem(
    items: Array<{
      code: string;
      name: string;
      description: string | null;
      category: string | null;
    }>,
    _context: ServiceContext,
  ): Promise<CreateResult<PermissionRow[]>> {
    return tryCreate<PermissionRow[]>(
      "/api/permission-catalog/bulk-system",
      { items },
    );
  },
};
